import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Receipt, X } from 'lucide-react';
import {
  allocateMilestoneSlice,
  billedMilestoneAmount,
  coveredStructureIds,
  formatInvoiceDollars,
  invoiceGroupsFromPackages,
  selectionFromPackages,
  unbilledChangeOrders,
  type BillablePackage,
  type ContractStructure,
  type MilestoneInvoiceRef,
  type MilestonePackageChoice,
  type PartialInstallmentId,
  type SliceCoAdjustment,
} from '../../utils/partialInvoiceAllocation';
import { installmentBillingFigures } from '../../utils/portalStatement';
import './PartialInvoicePackageModal.css';

export interface PartialInvoiceSelection {
  structureIds: string[];
  structureGroupKeys: string[];
  structureGroupLabels: string[];
  omittedPackageLabels: string[];
  structureGroups: Array<{ key: string; label: string; structureIds: string[] }>;
  /**
   * Change orders to fold in at their full amount for this milestone.
   * Present (even when empty) on a partial invoice. Omitted when the invoice is the whole milestone.
   */
  foldChangeOrderRefs?: string[];
  /** Invoice dollars. The server recomputes this; the queue uses it for the tracked row. */
  amount?: number;
}

export interface PartialInvoicePackageModalProps {
  open: boolean;
  installmentId: PartialInstallmentId;
  milestoneLabel: string;
  milestoneDetail: string;
  baseAmount: number;
  milestoneAmount: number;
  coAdjustments?: SliceCoAdjustment[];
  packages: MilestonePackageChoice[];
  structures: ContractStructure[];
  invoices: MilestoneInvoiceRef[];
  onClose: () => void;
  onConfirm: (selection: PartialInvoiceSelection) => void;
}

const PartialInvoicePackageModal: React.FC<PartialInvoicePackageModalProps> = ({
  open,
  installmentId,
  milestoneLabel,
  milestoneDetail,
  baseAmount,
  milestoneAmount,
  coAdjustments,
  packages,
  structures,
  invoices,
  onClose,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedCos, setSelectedCos] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setSelected(packages.filter((pkg) => !pkg.disabledReason).map((pkg) => pkg.key));
    setSelectedCos(
      unbilledChangeOrders(coAdjustments, invoices, installmentId)
        .filter((line) => Math.round(line.amount) < 0)
        .map((line) => line.reference),
    );
  }, [open, packages, coAdjustments, invoices, installmentId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const enabledPackages: BillablePackage[] = useMemo(
    () => packages.filter((pkg) => !pkg.disabledReason).map((pkg) => ({
      key: pkg.key,
      label: pkg.label,
      structureIds: pkg.structureIds,
      contractValue: pkg.contractValue,
      sharedStructureIds: pkg.sharedStructureIds,
    })),
    [packages],
  );

  const openChangeOrders = useMemo(
    () => unbilledChangeOrders(coAdjustments, invoices, installmentId),
    [coAdjustments, invoices, installmentId],
  );

  // Live schedule amount minus every active invoice on this installment — same
  // helper the portal card uses for "Not yet billed".
  const remainingToBill = useMemo(
    () => installmentBillingFigures({
      amount: milestoneAmount,
      amountBilled: billedMilestoneAmount(invoices, installmentId),
    }).amountUnbilled,
    [milestoneAmount, invoices, installmentId],
  );
  const billedChangeOrders = useMemo(
    () => (coAdjustments || []).filter(
      (line) => Math.round(line.amount) !== 0 && !openChangeOrders.some((openLine) => openLine.reference === line.reference),
    ),
    [coAdjustments, openChangeOrders],
  );

  const coveredIds = useMemo(
    () => Array.from(coveredStructureIds(
      invoices,
      installmentId,
      structures.map((structure) => structure.structureId),
    )),
    [invoices, installmentId, structures],
  );

  const preview = useMemo(() => {
    const selection = selectionFromPackages(enabledPackages, selected, {
      allPackages: packages,
      allStructureIds: structures.map((structure) => structure.structureId),
      coveredStructureIds: coveredIds,
    });
    if (selection.structureIds.length === 0 && selectedCos.length === 0) return null;
    const omitted = enabledPackages
      .filter((pkg) => !selected.includes(pkg.key))
      .map((pkg) => pkg.label);
    return allocateMilestoneSlice({
      installmentId,
      baseAmount,
      coAdjustments,
      selectedChangeOrderRefs: selectedCos,
      milestoneAmount,
      structures,
      selectedStructureIds: selection.structureIds,
      existingInvoices: invoices,
      includedLabels: selection.labels,
      includedGroupKeys: selection.keys,
      omittedLabels: omitted,
      milestoneLabel,
    });
  }, [
    enabledPackages,
    selected,
    selectedCos,
    installmentId,
    baseAmount,
    coAdjustments,
    milestoneAmount,
    structures,
    packages,
    invoices,
    coveredIds,
    milestoneLabel,
  ]);

  if (!open) return null;

  const toggle = (key: string) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]));
  };
  const toggleCo = (reference: string) => {
    setSelectedCos((prev) => (
      prev.includes(reference) ? prev.filter((id) => id !== reference) : [...prev, reference]
    ));
  };

  const sharedNote = preview && preview.ok
    ? selectionFromPackages(enabledPackages, selected).sharedStructureIds
    : [];
  const structureName = (id: string) => structures.find((structure) => structure.structureId === id)?.structureName || id;
  const invoiceAmount = preview && preview.ok ? preview.slice.amount : 0;
  const canConfirm = !!preview && preview.ok && invoiceAmount > 0;

  return createPortal(
    <div className="pip-overlay" role="presentation" onClick={onClose}>
      <div
        className="pip-modal"
        role="dialog"
        aria-labelledby="pip-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="pip-head">
          <div>
            <h3 id="pip-title">Invoice part of {milestoneLabel}</h3>
            <p>{milestoneDetail}. Choose the structure groups, and choose which change orders to fold in. A change order left unchecked stays off this invoice.</p>
          </div>
          <button type="button" className="pip-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        <div className="pip-body">
          {packages.map((pkg) => {
            const disabled = !!pkg.disabledReason;
            const checked = selected.includes(pkg.key);
            return (
              <label key={pkg.key} className={`pip-package ${disabled ? 'is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked && !disabled}
                  disabled={disabled}
                  onChange={() => toggle(pkg.key)}
                />
                <span>
                  <strong>{pkg.label}</strong>
                  <span className="pip-package-meta">
                    {formatInvoiceDollars(pkg.contractValue)} contract value
                    {pkg.sharedStructureIds.length > 0
                      ? ` · shares ${pkg.sharedStructureIds.map(structureName).join(', ')}`
                      : ''}
                  </span>
                  {pkg.disabledReason && <span className="pip-package-note">{pkg.disabledReason}</span>}
                </span>
              </label>
            );
          })}
          {(openChangeOrders.length > 0 || billedChangeOrders.length > 0) && (
            <div className="pip-co-block">
              <h4>Change orders</h4>
              <p>Fold one in now, or leave it for a later invoice. The full amount for this milestone is included.</p>
              {openChangeOrders.map((line) => (
                <label key={line.reference} className="pip-package">
                  <input
                    type="checkbox"
                    checked={selectedCos.includes(line.reference)}
                    onChange={() => toggleCo(line.reference)}
                  />
                  <span>
                    <strong>{line.reference}</strong>
                    <span className="pip-package-meta">
                      {line.title} · {formatInvoiceDollars(line.amount)} on this milestone
                    </span>
                  </span>
                </label>
              ))}
              {billedChangeOrders.map((line) => (
                <label key={line.reference} className="pip-package is-disabled">
                  <input type="checkbox" checked disabled />
                  <span>
                    <strong>{line.reference}</strong>
                    <span className="pip-package-note">Already on an invoice</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          {sharedNote.length > 0 && (
            <p className="pip-warn">
              {sharedNote.map(structureName).join(', ')} is in more than one selected group and is counted once.
            </p>
          )}
          {preview && !preview.ok && <p className="pip-error"><AlertCircle size={14} /> {preview.error}</p>}
        </div>
        <footer className="pip-foot">
          <div>
            <strong>{formatInvoiceDollars(invoiceAmount)}</strong>
            <span> of the {formatInvoiceDollars(milestoneAmount)} {milestoneLabel}</span>
            {remainingToBill > 0 && remainingToBill < milestoneAmount ? (
              <span className="pip-remaining"> · {formatInvoiceDollars(remainingToBill)} not yet billed</span>
            ) : null}
          </div>
          <button type="button" className="pip-confirm" disabled={!canConfirm} onClick={() => {
            if (!preview || !preview.ok || preview.slice.fullMilestone) {
              onConfirm({
                structureIds: [],
                structureGroupKeys: [],
                structureGroupLabels: [],
                omittedPackageLabels: [],
                structureGroups: [],
                amount: milestoneAmount,
              });
              return;
            }
            onConfirm({
              structureIds: preview.slice.structureIds,
              structureGroupKeys: preview.slice.structureGroupKeys,
              structureGroupLabels: preview.slice.structureGroupLabels,
              omittedPackageLabels: enabledPackages
                .filter((pkg) => !selected.includes(pkg.key))
                .map((pkg) => pkg.label),
              structureGroups: invoiceGroupsFromPackages(enabledPackages, selected),
              foldChangeOrderRefs: preview.slice.coLines.map((line) => line.reference),
              amount: preview.slice.amount,
            });
          }}>
            <Receipt size={14} /> Create invoice
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default PartialInvoicePackageModal;
